import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { SiGoogle, SiApple } from "react-icons/si";
import { Checkbox } from "@/components/ui/checkbox";
import {
  getPostLoginDestination,
  getPostRegistrationDestination,
} from "@/lib/signup-flow";

type Step = "email" | "password" | "register" | "recover";

export default function LoginPage() {
  return <AuthEntryPage />;
}

export function AuthEntryPage({ signupOnly = false }: { signupOnly?: boolean }) {
  const safeReturnTo = () => {
    const value = new URLSearchParams(window.location.search).get("returnTo");
    if (!value || value.length > 2048 || /[\u0000-\u001f\u007f\\]/.test(value)) return null;
    let decoded = value;
    try {
      decoded = decodeURIComponent(decoded);
      decoded = decodeURIComponent(decoded);
    } catch {
      return null;
    }
    if (!decoded.startsWith("/") || decoded.startsWith("//") || /[\u0000-\u001f\u007f\\]/.test(decoded)) return null;
    const parsed = new URL(value, window.location.origin);
    return parsed.origin === window.location.origin
      ? `${parsed.pathname}${parsed.search}${parsed.hash}`
      : null;
  };
  const [step, setStep] = useState<Step>(signupOnly ? "register" : "email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [profileName, setProfileName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [providerHint, setProviderHint] = useState<string | null>(null);
  const [accountHasPassword, setAccountHasPassword] = useState(true);
  const [recoverySent, setRecoverySent] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    if (error === "google_failed") {
      toast({ title: "Google sign-in failed", description: "Please try again or use email instead.", variant: "destructive" });
    } else if (error === "apple_coming_soon") {
      toast({ title: "Apple Sign-In coming soon", description: "Please use Google or email to sign in for now." });
    } else if (error === "login_failed" || error === "callback_failed") {
      toast({ title: "Sign-in failed", description: "Something went wrong. Please try again.", variant: "destructive" });
    }
  }, []);

  const checkEmailMutation = useMutation({
    mutationFn: async (email: string) => {
      const res = await fetch("/api/auth/check-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to check email");
      }
      return res.json();
    },
    onSuccess: (data) => {
      if (data.exists) {
        setAccountHasPassword(Boolean(data.hasPassword));
        if (data.hasPassword) {
          setStep("password");
        } else {
          setProviderHint(data.provider);
          setStep("password");
        }
      } else {
        setStep("register");
      }
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const loginMutation = useMutation({
    mutationFn: async (data: { email: string; password: string }) => {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const result = await res.json();
        throw new Error(result.message || "Login failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      const pendingTier = localStorage.getItem("tpt_pending_tier");
      const returnTo = safeReturnTo();
      navigate(pendingTier ? "/settings" : getPostLoginDestination(returnTo));
    },
    onError: (err: Error) => {
      toast({ title: "Login failed", description: err.message, variant: "destructive" });
    },
  });

  const recoveryMutation = useMutation({
    mutationFn: async (address: string) => {
      const response = await fetch("/api/auth/password-reset/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: address }),
      });
      if (!response.ok) throw new Error("Unable to request a reset link right now. Please try again later.");
    },
    onSuccess: () => setRecoverySent(true),
    onError: (error: Error) => toast({ title: "Could not request a reset link", description: error.message, variant: "destructive" }),
  });

  const registerMutation = useMutation({
    mutationFn: async (data: { email: string; password: string; firstName: string; lastName: string; market: string }) => {
      const partnerSource = localStorage.getItem("referral_source") || undefined;
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...data, ...(partnerSource ? { partnerSource } : {}) }),
      });
      if (!res.ok) {
        const result = await res.json();
        throw new Error(result.message || "Registration failed");
      }
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      try {
        (window as any).gtag("event", "conversion", { send_to: "AW-18031966412/UdfpCJHNj40cEMzxp5ZD" });
      } catch (_) {}
      const pendingTier = localStorage.getItem("tpt_pending_tier");
      const fbEventId = data?.fbEventId;
      const returnTo = safeReturnTo();
      const signupPath = getPostRegistrationDestination(returnTo, fbEventId);
      navigate(pendingTier ? "/settings" : signupPath);
    },
    onError: (err: Error) => {
      toast({ title: "Registration failed", description: err.message, variant: "destructive" });
    },
  });

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    checkEmailMutation.mutate(email.trim());
  };

  const handleLoginSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;
    loginMutation.mutate({ email, password });
  };

  const handleRegisterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (signupOnly && (!email.trim() || !termsAccepted)) return;
    if (!password || password.length < 6) {
      toast({ title: "Error", description: "Password must be at least 6 characters.", variant: "destructive" });
      return;
    }
    registerMutation.mutate({ email: signupOnly ? email.trim() : email, password, firstName: profileName, lastName: companyName, market: "uk" });
  };

  const handleOAuth = (provider: string) => {
    const returnTo = safeReturnTo();
    const params = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : "";
    window.location.href = `/api/auth/${provider}${params}`;
  };

  const goBack = () => {
    setStep("email");
    setPassword("");
    setProviderHint(null);
    setRecoverySent(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-9 h-9 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-lg">T</span>
            </div>
            <span className="text-2xl font-bold text-foreground">The Proper Tool</span>
          </div>
        </div>

        <Card>
          <CardContent className="p-8">
            {step === "email" && (
              <>
                <h1 className="text-2xl font-bold text-foreground mb-2" data-testid="text-login-title">
                  Sign in or create an account
                </h1>
                <p className="text-sm text-muted-foreground mb-6">
                  Access your The Proper Tool account to manage quotes, clients, and more.
                </p>

                <form onSubmit={handleEmailSubmit}>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="email" className="text-sm font-medium">
                        Email address
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-1.5"
                        autoFocus
                        data-testid="input-email"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={checkEmailMutation.isPending || !email.trim()}
                      data-testid="button-continue-email"
                    >
                      {checkEmailMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Continue with email"
                      )}
                    </Button>
                  </div>
                </form>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-card px-3 text-muted-foreground">
                      or continue with
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    variant="outline"
                    className="w-full h-11 font-medium gap-2.5"
                    onClick={() => handleOAuth("google")}
                    data-testid="button-google-login"
                  >
                    <SiGoogle className="h-4 w-4" />
                    Continue with Google
                  </Button>
                  <p className="text-center text-xs text-muted-foreground leading-relaxed">
                    By continuing with Google you agree to our{" "}
                    <a href="/terms" target="_blank" className="underline hover:text-foreground transition-colors">Terms and Conditions</a>
                    {" "}and{" "}
                    <a href="/privacy" target="_blank" className="underline hover:text-foreground transition-colors">Privacy Policy</a>.
                  </p>
                  <Button
                    variant="outline"
                    className="w-full h-11 font-medium gap-2.5 opacity-50 cursor-not-allowed"
                    disabled
                    title="Apple Sign-In coming soon"
                    data-testid="button-apple-login"
                  >
                    <SiApple className="h-4 w-4" />
                    Continue with Apple
                    <span className="ml-auto text-xs font-normal text-muted-foreground">Coming soon</span>
                  </Button>
                </div>
              </>
            )}

            {step === "password" && (
              <>
                <button
                  onClick={goBack}
                  className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover-elevate rounded-md px-2 py-1 -ml-2"
                  data-testid="button-back"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
                <h1 className="text-2xl font-bold text-foreground mb-2" data-testid="text-password-title">
                  Enter your password
                </h1>
                <p className="text-sm text-muted-foreground mb-6">
                  Signing in as <strong>{email}</strong>
                </p>

                {providerHint && providerHint !== "email" && (
                  <div className="bg-muted/50 rounded-md p-3 mb-4 text-sm text-muted-foreground">
                    This account was created with {providerHint}. {accountHasPassword
                      ? "You can also sign in using that provider."
                      : "It does not have a password yet. Sign in with that provider, or request a link to set a password."}
                  </div>
                )}

                {providerHint === "google" && (
                  <Button
                    variant={accountHasPassword ? "outline" : "default"}
                    className="w-full mb-4 gap-2"
                    onClick={() => handleOAuth("google")}
                    data-testid="button-google-login-password-step"
                  >
                    <SiGoogle className="h-4 w-4" /> Continue with Google
                  </Button>
                )}

                {accountHasPassword && <form onSubmit={handleLoginSubmit}>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="password" className="text-sm font-medium">
                        Password
                      </Label>
                      <div className="relative mt-1.5">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoFocus
                          data-testid="input-password"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                          onClick={() => setShowPassword(!showPassword)}
                          data-testid="button-toggle-password"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={loginMutation.isPending || !password}
                      data-testid="button-sign-in"
                    >
                      {loginMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Sign in"
                      )}
                    </Button>
                  </div>
                </form>}
                <button
                  type="button"
                  onClick={() => { setStep("recover"); setRecoverySent(false); }}
                  className="block mx-auto mt-4 text-sm text-primary hover:underline"
                  data-testid="button-forgot-password"
                >
                  {accountHasPassword ? "Forgot your password?" : "Email me a link to set a password"}
                </button>
              </>
            )}

            {step === "recover" && (
              <>
                <button
                  type="button"
                  onClick={goBack}
                  className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover-elevate rounded-md px-2 py-1 -ml-2"
                >
                  <ArrowLeft className="h-4 w-4" /> Back to sign in
                </button>
                <h1 className="text-2xl font-bold text-foreground mb-2">Reset your password</h1>
                <p className="text-sm text-muted-foreground mb-6">
                  Enter your account email and we’ll send a link to reset or set a password. The link expires after 30 minutes.
                </p>
                {recoverySent ? (
                  <div role="status" className="rounded-md border bg-muted/40 p-4 text-sm space-y-2">
                    <p>If an account exists for <strong>{email}</strong>, we’ve sent a reset link.</p>
                    <p className="text-muted-foreground">Check your inbox and spam folder. If you signed up with Google, you can also sign in with Google.</p>
                  </div>
                ) : (
                  <form onSubmit={(event) => {
                    event.preventDefault();
                    if (email.trim()) recoveryMutation.mutate(email.trim());
                  }}>
                    <Label htmlFor="recovery-email">Email address</Label>
                    <Input
                      id="recovery-email"
                      type="email"
                      autoComplete="email"
                      required
                      autoFocus
                      className="mt-1.5"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      data-testid="input-recovery-email"
                    />
                    <Button type="submit" className="w-full mt-4" disabled={recoveryMutation.isPending} data-testid="button-send-reset-link">
                      {recoveryMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
                    </Button>
                  </form>
                )}
              </>
            )}

            {step === "register" && (
              <>
                {!signupOnly && <button
                  onClick={goBack}
                  className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover-elevate rounded-md px-2 py-1 -ml-2"
                  data-testid="button-back-register"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>}
                <h1 className="text-2xl font-bold text-foreground mb-2" data-testid="text-register-title">
                  Create your account
                </h1>
                <p className="text-sm text-muted-foreground mb-6">
                  {signupOnly
                    ? "14-day free trial · 5 quotes included · No card needed"
                    : <>Setting up account for <strong>{email}</strong></>}
                </p>

                {signupOnly && (
                  <div className="mb-6">
                    <Button
                      variant="outline"
                      className="w-full min-h-11 font-medium gap-2.5"
                      onClick={() => handleOAuth("google")}
                      data-testid="button-google-signup"
                    >
                      <SiGoogle className="h-4 w-4" />
                      Continue with Google
                    </Button>
                    <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
                      By continuing with Google you agree to our{" "}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms and Conditions</a>,{" "}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a>{" "}
                      and <a href="/cookies" target="_blank" rel="noopener noreferrer" className="underline">Cookie Policy</a>.
                    </p>
                    <p className="mt-5 text-center text-xs text-muted-foreground">Or create an account with email</p>
                  </div>
                )}

                <form onSubmit={handleRegisterSubmit}>
                  <div className="space-y-4">
                    {signupOnly && (
                      <div>
                        <Label htmlFor="signup-email" className="text-sm font-medium">
                          Email address
                        </Label>
                        <Input
                          id="signup-email"
                          type="email"
                          autoComplete="email"
                          required
                          placeholder="Enter your email address"
                          value={email}
                          onChange={(event) => setEmail(event.target.value)}
                          className="mt-1.5 min-h-11"
                          data-testid="input-signup-email"
                        />
                      </div>
                    )}
                    <div>
                      <Label htmlFor="profileName" className="text-sm font-medium">
                        Profile name
                      </Label>
                      <Input
                        id="profileName"
                        placeholder="Your name"
                        value={profileName}
                        onChange={(e) => setProfileName(e.target.value)}
                        className="mt-1.5"
                        autoFocus={!signupOnly}
                        data-testid="input-profile-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="companyName" className="text-sm font-medium">
                        Company name <span className="text-muted-foreground font-normal">(optional)</span>
                      </Label>
                      <Input
                        id="companyName"
                        placeholder="Your company or trading name"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        className="mt-1.5"
                        data-testid="input-company-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="newPassword" className="text-sm font-medium">
                        Create password
                      </Label>
                      <div className="relative mt-1.5">
                        <Input
                          id="newPassword"
                          type={showPassword ? "text" : "password"}
                          placeholder="At least 6 characters"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoComplete={signupOnly ? "new-password" : undefined}
                          data-testid="input-new-password"
                        />
                        <button
                          type="button"
                          className={signupOnly
                            ? "absolute right-0 top-1/2 -translate-y-1/2 min-h-11 min-w-11 flex items-center justify-center text-muted-foreground"
                            : "absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          onClick={() => setShowPassword(!showPassword)}
                          data-testid="button-toggle-new-password"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="rounded-lg border bg-muted/40 p-3 space-y-1.5 text-sm">
                      <a href="/terms" target="_blank" className="flex items-center justify-between hover:text-primary transition-colors group">
                        <span>Terms and Conditions</span>
                        <span className="text-xs text-muted-foreground group-hover:text-primary">Read &rarr;</span>
                      </a>
                      <div className="border-t" />
                      <a href="/privacy" target="_blank" className="flex items-center justify-between hover:text-primary transition-colors group">
                        <span>Privacy Policy</span>
                        <span className="text-xs text-muted-foreground group-hover:text-primary">Read &rarr;</span>
                      </a>
                      <div className="border-t" />
                      <a href="/cookies" target="_blank" className="flex items-center justify-between hover:text-primary transition-colors group">
                        <span>Cookie Policy</span>
                        <span className="text-xs text-muted-foreground group-hover:text-primary">Read &rarr;</span>
                      </a>
                    </div>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="terms-accept"
                        checked={termsAccepted}
                        onCheckedChange={(v) => setTermsAccepted(!!v)}
                        data-testid="checkbox-terms-accept"
                      />
                      <label htmlFor="terms-accept" className="text-sm leading-relaxed cursor-pointer">
                        I have read and agree to the Terms and Conditions, Privacy Policy, and Cookie Policy.
                      </label>
                    </div>
                    <Button
                      type="submit"
                       className={signupOnly ? "w-full min-h-11" : "w-full"}
                       disabled={registerMutation.isPending || !password || !termsAccepted || (signupOnly && !email.trim())}
                      data-testid="button-create-account"
                    >
                      {registerMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Create account"
                      )}
                    </Button>
                  </div>
                </form>
                {signupOnly && (
                  <p className="mt-6 text-center text-sm text-muted-foreground">
                    Already have an account?{" "}
                    <Link href="/login" className="inline-flex min-h-11 items-center font-medium text-primary hover:underline" data-testid="link-existing-account">
                      Sign in
                    </Link>
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          By signing in or creating an account, you agree to our{" "}
          <a href="/terms" className="underline hover:text-foreground transition-colors">Terms and Conditions</a>
          {" "}and{" "}
          <a href="/privacy" className="underline hover:text-foreground transition-colors">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
}
